import React, { useState, useEffect } from "react";
import { Button, Modal, Stack, Form, Accordion } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import { useSettings } from "../contexts/SettingsProvider";
import UserConfirmModal from "./UserConfirmModal";
import { useInfo } from "../contexts/InfoProvider";
import { useWidth } from "../contexts/WidthProvider";
import ToggleSwitch from "./ToggleSwitch";
import { useTheme } from "../contexts/ThemeProvider";
import { useNavigate } from "react-router-dom";
import { useBoatInfo } from "../contexts/BoatInfoProvider";
import useAuth from "../hooks/useAuth";
import { useConversations } from "../contexts/ConversationsProvider";
import { useShips } from "../contexts/ShipsProvider";

export default function SettingsModal({ show, onHide }) {
  const navigate = useNavigate();
  const { updateBoatInfo } = useBoatInfo();
  const { setAuth } = useAuth();
  const { t, i18n } = useTranslation();
  const { isDarkTheme } = useTheme();
  const { useMobileLayout } = useWidth();
  const {
    speechEnabled,
    setSpeechEnabled,
    language,
    setLanguage,
    onlyIsideMessage,
    setOnlyIsideMessage,
    advancedMessaging,
    setAdvancedMessaging,
    aistype,
    setAistype,
    supportedAistypes,
    aisStatus,
    resetSettings,
    sendDummyPosition,
    setSendDummyPosition,
  } = useSettings();
  const [lang, setLang] = useState(language || i18n.language);
  const [checked, setChecked] = useState(speechEnabled);
  const [checkedIside, setCheckedIside] = useState(onlyIsideMessage);
  const [checkedAdvancedMsg, setCheckedAdvancedMsg] =
    useState(advancedMessaging);
  const [checkDummyPosition, setCheckDummyPosition] =
    useState(sendDummyPosition);
  const [selectedAistype, setSelectedAistype] = useState(aistype);
  const [openResetModal, setOpenResetModal] = useState(false);
  const { OpenInfoModal } = useInfo();
  const { resetConversations } = useConversations();
  const { resetShipsList } = useShips();

  useEffect(() => {
    setLang(language || i18n.language);
    setChecked(speechEnabled);
    setCheckedIside(onlyIsideMessage);
    setCheckedAdvancedMsg(advancedMessaging);
    setSelectedAistype(aistype);
  }, [
    speechEnabled,
    language,
    onlyIsideMessage,
    advancedMessaging,
    i18n.language,
    aistype,
  ]);

  function confirmReset() {
    resetSettings();
    resetConversations();
    resetShipsList();
    setOpenResetModal(false);
    onCancel();
  }

  function onCancel() {
    setLang(language || i18n.language);
    setChecked(speechEnabled);
    setCheckedIside(onlyIsideMessage);
    setCheckedAdvancedMsg(advancedMessaging);
    setCheckDummyPosition(sendDummyPosition);
    setSelectedAistype(aistype);
    onHide();
  }

  function closeResetModal() {
    setOpenResetModal(false);
  }

  function applySettings() {
    if (lang !== language) {
      setLanguage(lang);
    }
    if (checked !== speechEnabled) {
      setSpeechEnabled(checked);
    }
    if (checkedIside !== onlyIsideMessage) {
      setOnlyIsideMessage(checkedIside);
    }
    if (checkedAdvancedMsg !== advancedMessaging) {
      setAdvancedMessaging(checkedAdvancedMsg);
    }
    if (checkDummyPosition !== sendDummyPosition) {
      setSendDummyPosition(checkDummyPosition);
    }
    if (selectedAistype !== aistype) {
      setAistype(selectedAistype);
    }
    onHide();
  }

  function onChangeBoat() {
    updateBoatInfo({});
    navigate("/");
  }

  function onLogout() {
    updateBoatInfo({});
    // if used in more components, this should be in context
    // axios to /logout endpoint
    setAuth({});
    navigate("/login");
  }

  return (
    <>
      <Modal
        className={`${!useMobileLayout ? "desktop" : ""}`}
        show={show}
        onHide={onCancel}
        centered
      >
        <Modal.Header closeButton closeVariant={isDarkTheme ? "white" : null}>
          <div className="title">
            <div className="title-text">{t("settings")}</div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Stack direction="vertical" gap="2" className="body settings">
            <Stack direction="horizontal" gap="2" className="">
              <div className="modal-button primary" onClick={onChangeBoat}>
                <FontAwesomeIcon size="sm" icon="ship" className="me-2" />
                {t("change_boat")}
              </div>
              <div className="modal-button primary" onClick={onLogout}>
                <FontAwesomeIcon
                  size="sm"
                  icon="arrow-right-from-bracket"
                  className="me-2"
                />
                {t("logout")}
              </div>
            </Stack>
            <div>
              <Form>
                <Form.Label>
                  <h5>{t("select_language")}</h5>
                </Form.Label>
                <Form.Select
                  onChange={(e) => setLang(e.target.value)}
                  value={lang}
                >
                  {i18n.supportedLanguages.map((l, index) => (
                    <option key={index} value={l}>
                      {t(l)}
                    </option>
                  ))}
                </Form.Select>
              </Form>
              <hr />
            </div>
            <div>
              <h5>{t("advanced_messaging")}</h5>
              <ToggleSwitch
                name="advancedMsgToggle"
                falseLabel={t("inactive")}
                trueLabel={t("active")}
                defaultValue={checkedAdvancedMsg}
                onChange={(value) => {
                  setCheckedAdvancedMsg(value);
                }}
              />
              <hr />
            </div>
            <div>
              <h5>{t("select_speech")}</h5>
              <ToggleSwitch
                name="speechToggle"
                falseLabel={t("inactive")}
                trueLabel={t("active")}
                defaultValue={checked}
                onChange={(value) => {
                  setChecked(value);
                }}
              />
              <hr />
            </div>
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <h5>{t("developer_option")}</h5>
                </Accordion.Header>
                <Accordion.Body>
                  <div>
                    <h5>{t("iside_only_messages")}</h5>
                    <ToggleSwitch
                      name="isideToggle"
                      falseLabel={t("inactive")}
                      trueLabel={t("active")}
                      defaultValue={checkedIside}
                      onChange={(value) => {
                        setCheckedIside(value);
                      }}
                    />
                    <hr />
                  </div>
                  <div>
                    <h5>{t("send_dummy_position")}</h5>
                    <ToggleSwitch
                      name="sendDummyPositionToggle"
                      falseLabel={t("inactive")}
                      trueLabel={t("active")}
                      defaultValue={checkDummyPosition}
                      onChange={(value) => {
                        setCheckDummyPosition(value);
                      }}
                    />
                    <hr />
                  </div>
                  <div style={{ display: aisStatus ? "none" : "block" }}>
                    <h5>{t("ais_not_enabled")}</h5>
                    <hr />
                  </div>
                  <div style={{ display: aisStatus ? "block" : "none" }}>
                    <div>
                      <Form>
                        <Form.Label>
                          <h5>{t("select_aistype")}</h5>
                        </Form.Label>
                        <Form.Select
                          onChange={(e) => setSelectedAistype(e.target.value)}
                          value={selectedAistype}
                        >
                          {supportedAistypes.map((t, index) => (
                            <option key={index} value={t}>
                              {t}
                            </option>
                          ))}
                        </Form.Select>
                      </Form>
                      <hr />
                    </div>
                  </div>
                  <div>
                    <h5>{t("reset_application_text")}</h5>
                    <div
                      className="modal-button danger"
                      onClick={() => setOpenResetModal(true)}
                    >
                      {t("reset_application")}
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <div className="m-auto">
              <Button variant="link" size="sm" onClick={() => OpenInfoModal()}>
                {t("about_iside_project")}
              </Button>
            </div>
          </Stack>
        </Modal.Body>
        <Modal.Footer>
          <Stack direction="horizontal" gap="2" className="">
            <div className="modal-button secondary" onClick={onCancel}>
              {t("cancel")}
            </div>
            <div className="modal-button" onClick={applySettings}>
              {t("confirm_text")}
            </div>
          </Stack>
        </Modal.Footer>
      </Modal>
      <Modal
        className={`${!useMobileLayout ? "desktop" : ""}`}
        show={openResetModal}
        onHide={closeResetModal}
        centered
      >
        <UserConfirmModal
          onConfirm={confirmReset}
          onCancel={closeResetModal}
          title={t("reset_message")}
          text={t("reset_question")}
          confirmButtonText={t("ok")}
          cancelButtonText={t("cancel")}
        />
      </Modal>
    </>
  );
}
